import { type Actr } from '@opcentral/typr-lib';

/**
 * ActivitySlug.ts - ActivitySlug Model
 * 
 * CHANGE HISTORY:
 * - 251226_0854 [active] danielbeer: Remove @ts-nocheck and fix type errors
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
// import { generateId } from '@opcentral/core-lib';
const generateId = () => Math.random().toString(36).substring(2, 15);


interface ActivitySlugData {
  id?: string;
  timestamp?: number;
  userId?: string;
  sessionId?: string;
  description?: string;
  type?: string;
  context?: Record<string, any>;
  metadata?: Record<string, any>;
}

/**
 * ActivitySlug class
 */
export class ActivitySlug {
  id: string;
  timestamp: number | string;
  userId?: string;
  sessionId?: string;
  description: string;
  type: string;
  context: Record<string, any>;
  metadata: Record<string, any>;

  constructor(data: ActivitySlugData = {}) {
    this.id = data.id || generateId();
    this.timestamp = data.timestamp || Date.now();
    this.userId = data.userId || 'system';
    this.sessionId = data.sessionId;
    this.description = data.description || '';
    this.type = data.type || 'action';
    this.context = data.context || {};
    this.metadata = data.metadata || {};
  }
  
  /**
   * Generate slug string
   * Format: [ISO_timestamp]_[userId]_[sessionId]_[description]
   */
  toString(): string {
    const timestamp = typeof this.timestamp === 'number' 
      ? new Date(this.timestamp).toISOString() 
      : this.timestamp;
    const timestampStr = timestamp.replace(/[:.]/g, '-');
    const shortSession = this.sessionId ? this.sessionId.substring(0, 8) : 'no-session';
    const desc = this.description.toLowerCase().replace(/\s+/g, '-').substring(0, 40);
    return `${timestampStr}_${this.userId}_${shortSession}_${desc}`;
  }
  
  /**
   * Generate filename-safe slug
   */
  toFilename(): string {
    const isoTimestamp = typeof this.timestamp === 'number' 
      ? new Date(this.timestamp).toISOString() 
      : this.timestamp;
    const timestamp = isoTimestamp.split('T')[0];
    const time = isoTimestamp.split('T')[1].split('.')[0].replace(/:/g, '-');
    const shortSession = this.sessionId ? this.sessionId.substring(0, 8) : 'no-session';
    const desc = this.description.toLowerCase().replace(/[^a-z0-9]+/g, '-').substring(0, 40);
    return `${timestamp}_${time}_${this.userId}_${shortSession}_${desc}`;
  }
  
  /**
   * Generate markdown header
   */
  toMarkdownHeader(): string {
    return `---
slug: ${this.toString()}
timestamp: ${this.timestamp}
user: ${this.userId}
session: ${this.sessionId}
type: ${this.type}
description: ${this.description}
---`;
  }
  
  /**
   * Parse slug from string
   */
  static fromString(slugString: string): ActivitySlug {
    const parts = slugString.split('_');
    if (parts.length < 4) {
      throw new Error('Invalid slug format');
    }
    
    return new ActivitySlug({
      timestamp: Date.parse(parts[0].replace(/-/g, ':')),
      userId: parts[1],
      sessionId: parts[2],
      description: parts.slice(3).join('_').replace(/-/g, ' ')
    });
  }
  
  toJSON(): ActivitySlugData {
    return {
      id: this.id,
      timestamp: typeof this.timestamp === 'number' ? this.timestamp : Date.parse(this.timestamp),
      userId: this.userId,
      sessionId: this.sessionId,
      description: this.description,
      type: this.type,
      context: this.context,
      metadata: this.metadata
    };
  }
  
  static fromJSON(json: ActivitySlugData): ActivitySlug {
    return new ActivitySlug(json);
  }
}

export default ActivitySlug;
